import React, { useRef, useState, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { OverlayPanel } from 'primereact/overlaypanel';
import PhoneInput from 'react-phone-number-input';
import classNames from 'classnames';
import { ReactSortable } from 'react-sortablejs';
import { CustomButton } from '../../../../components/CustomButton';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { isValidURL } from '../../../../utils/templateUtils';
import { ReactComponent as SixDot_icon } from '../../../../svg/sixDot.svg';
import styles from '../stylesheets/button-editor.module.css';
import { Button } from 'primereact/button';
import { BUTTON_LIMITS } from '../../../../utils/templateUtils';

const QuickReplySection = ({
  templateButtons,
  handleQuickReplyChange,
  removeButton,
  addQuickReplyBtn,
  getButtonTypeCounts,
}) => (
  <div className="border-1 border-round surface-border mb-3">
    <div
      className="p-2 mb-2 flex align-items-center gap-3 drag-handle"
      style={{ backgroundColor: '#F6F6F6', cursor: 'grab' }}
    >
      <SixDot_icon />
      <h4 className="m-0 text-sm">Quick Replies</h4>
    </div>
    <div className="p-2 mb-2">
      <div className="flex justify-content-end mb-2">
        <Button
          label="Quick Reply"
          icon="pi pi-plus"
          type="button"
          onClick={addQuickReplyBtn}
          disabled={
            getButtonTypeCounts().total >= 10 ||
            getButtonTypeCounts().quickReplyCount >= getButtonTypeCounts().maxQuickReplies
          }
          style={{ background: '#F1F1F2', color: '#005068', border: '0px' }}
          className="skalebot-button"
        />
      </div>

      {templateButtons
        .filter((btn) => btn.type === 'QUICK_REPLY')
        .map((item) => (
          <div key={item.id} className="flex align-items-center gap-2 my-2 p-2">
            <InputText
              placeholder="Enter Label (Max 17 Char)"
              value={item?.text}
              onChange={(e) => handleQuickReplyChange(e, item?.id)}
              maxLength={17}
              className="w-full"
            />
            <i className="pi pi-trash cursor-pointer" onClick={() => removeButton(item?.id)}></i>
          </div>
        ))}
    </div>
  </div>
);

const CTASection = ({ templateButtons, handleButtonEdit, removeButton }) => (
  <div className="border-1 border-round surface-border mb-3">
    <div
      className="p-2 mb-2 flex align-items-center gap-3 drag-handle"
      style={{ backgroundColor: '#F6F6F6', cursor: 'grab' }}
    >
      <SixDot_icon />
      <h4 className="m-0 text-sm">Call-To-Action Buttons</h4>
    </div>
    {templateButtons
      .filter((btn) => btn.type === 'PHONE_NUMBER' || btn.type === 'URL')
      .map((item) => (
        <div key={item.id} className="flex flex-row gap-2 w-full mb-2 p-2">
          <div className="flex flex-column w-4">
            <InputText
              placeholder="Enter Label (Max 17 Char)"
              value={item.text}
              onChange={(e) => handleButtonEdit(e, item.id, 'text')}
              maxLength={17}
              required
              className={`${classNames({
                'p-invalid': item?.text.trim().length === 0,
              })} w-full`}
            />
            {!item.text?.trim() && (
              <span className="text-red-500 text-sm">Please enter label for button.</span>
            )}
          </div>

          {item.type === 'PHONE_NUMBER' && (
            <div className="flex gap-2 w-8">
              <div className="flex flex-column w-full">
                <PhoneInput
                  name="phone_number"
                  defaultCountry="IN"
                  placeholder="Enter phone number"
                  value={item.phone_number}
                  international
                  onChange={(e) => handleButtonEdit(e, item.id, 'phone_number')}
                  className={`${classNames({
                    'p-invalid': !item.phone_number,
                  })} flex-grow-1 w-full`}
                />
                {(!item.phone_number || !isValidPhoneNumber(item.phone_number)) && (
                  <span className="text-red-500 text-sm block">Please enter a valid number</span>
                )}
              </div>
              <i
                className="pi pi-trash cursor-pointer mt-2"
                onClick={() => removeButton(item.id)}
              ></i>
            </div>
          )}

          {item.type === 'URL' && (
            <div className="flex gap-2 w-8">
              <div className="flex flex-column w-full">
                <InputText
                  placeholder="Enter URL"
                  value={item.url}
                  onChange={(e) => handleButtonEdit(e, item.id, 'url')}
                  className={`${classNames({
                    'p-invalid': !isValidURL(item.url),
                  })} flex-grow-1 w-full`}
                />
                {!isValidURL(item.url) && (
                  <span className="text-red-500 text-sm block">Please enter a valid URL</span>
                )}
              </div>
              <i
                className="pi pi-trash cursor-pointer mt-2"
                onClick={() => removeButton(item.id)}
              ></i>
            </div>
          )}
        </div>
      ))}
  </div>
);

const ButtonEditor = ({
  templateButtons,
  setTemplateButtons,
  handleQuickReplyChange,
  handleButtonEdit,
  removeButton,
  addQuickReplyBtn,
  handleAddCTA,
  getButtonTypeCounts,
  mode = 'MARKETING',
}) => {
  const ctaPanelRef = useRef(null);
  const [sectionOrder, setSectionOrder] = useState([
    { id: 'CTA', type: 'CTA' },
    { id: 'QUICK_REPLY', type: 'QUICK_REPLY' },
  ]);

  const limits = BUTTON_LIMITS[mode];

  // Update button order when section order changes
  useEffect(() => {
    if (templateButtons.length === 0) return;

    const reorderedButtons = [];

    sectionOrder.forEach((section) => {
      if (section.type === 'CTA') {
        // Add all CTA buttons (PHONE_NUMBER and URL)
        const ctaButtons = templateButtons.filter(
          (btn) => btn.type === 'PHONE_NUMBER' || btn.type === 'URL',
        );
        reorderedButtons.push(...ctaButtons);
      } else if (section.type === 'QUICK_REPLY') {
        // Add all Quick Reply buttons
        const quickReplyButtons = templateButtons.filter((btn) => btn.type === 'QUICK_REPLY');
        reorderedButtons.push(...quickReplyButtons);
      }
    });

    // Only update if order actually changed
    const currentOrder = templateButtons.map((btn) => btn.id).join(',');
    const newOrder = reorderedButtons.map((btn) => btn.id).join(',');

    if (currentOrder !== newOrder && reorderedButtons.length > 0) {
      setTemplateButtons(reorderedButtons);
    }
  }, [sectionOrder]);

  const getSectionComponent = (type) => {
    switch (type) {
      case 'CTA':
        return (
          <CTASection
            templateButtons={templateButtons}
            handleButtonEdit={handleButtonEdit}
            removeButton={removeButton}
          />
        );
      case 'QUICK_REPLY':
        return (
          <QuickReplySection
            templateButtons={templateButtons}
            handleQuickReplyChange={handleQuickReplyChange}
            removeButton={removeButton}
            addQuickReplyBtn={addQuickReplyBtn}
            getButtonTypeCounts={getButtonTypeCounts}
          />
        );
      default:
        return null;
    }
  };

  const hasCTAButtons = templateButtons.some(
    (btn) => btn.type === 'PHONE_NUMBER' || btn.type === 'URL',
  );
  const hasQuickReplyButtons = templateButtons.some((btn) => btn.type === 'QUICK_REPLY');

  return (
    <div className="w-full">
      <div className="flex align-items-center gap-3">
        <label className="inline-block">Call to action buttons</label>
        <i
          className="pi pi-info-circle cursor-pointer open-doc-icon"
          data-pr-tooltip="You can add up to 10 buttons. Quick Replies can use all 10 if no URLs (max 2) or phone numbers (max 1) are included."
          data-pr-position="top"
          style={{ fontSize: '1.2rem' }}
          color="text-gray-600"
        />
      </div>

      <div className="flex flex-row flex-wrap gap-3 my-2">
        <div className="w-full">
          <div className="flex justify-content-start gap-3 my-2">
            <CustomButton
              label="Add CTA"
              icon="pi pi-cog"
              onClick={(e) => ctaPanelRef.current.toggle(e)}
              disabled={
                getButtonTypeCounts().total >= limits.maxTotal ||
                getButtonTypeCounts().totalCTA >= limits.maxCTA
              }
            />
            <CustomButton
              label="Quick Reply"
              icon="pi pi-plus"
              onClick={addQuickReplyBtn}
              disabled={
                getButtonTypeCounts().total >= limits.maxTotal ||
                getButtonTypeCounts().quickReplyCount >= limits.maxQuickReplies
              }
            />
          </div>

          <OverlayPanel ref={ctaPanelRef}>
            <div className="flex flex-column gap-2 p-2 w-12rem">
              <CustomButton
                label="Phone Number"
                icon="pi pi-phone"
                varient="text"
                className="w-full"
                disabled={getButtonTypeCounts().phoneCount >= limits.maxPhone}
                severity="info"
                onClick={() => {
                  handleAddCTA('PHONE_NUMBER');
                  ctaPanelRef.current.hide();
                }}
              />
              <CustomButton
                label="URL"
                icon="pi pi-link"
                varient="text"
                className="w-full"
                disabled={getButtonTypeCounts().urlCount >= limits.maxURL}
                severity="help"
                onClick={() => {
                  handleAddCTA('URL');
                  ctaPanelRef.current.hide();
                }}
              />
            </div>
          </OverlayPanel>
        </div>
      </div>

      {templateButtons?.length > 0 && (hasCTAButtons || hasQuickReplyButtons) && (
        <div className={`${styles.base_background}`}>
          <div className="flex justify-content-between align-items-center mb-2">
            <label className="inline-block">Button Configuration</label>
            <div
              className="p-1"
              style={{
                border: '0.5px solid #000',
                borderTopLeftRadius: '50px',
                borderBottomLeftRadius: '50px',
                borderTopRightRadius: '50px',
                borderBottomRightRadius: '50px',
              }}
            >
              <small>
                {' '}
                {`Total : ${getButtonTypeCounts().total} / Max ${limits.maxTotal} buttons`}{' '}
              </small>
            </div>
          </div>
          <ReactSortable
            list={sectionOrder}
            setList={setSectionOrder}
            animation={200}
            handle=".drag-handle"
            ghostClass="sortable-ghost"
            dragClass="sortable-drag"
          >
            {sectionOrder
              .filter((section) => {
                if (section.type === 'CTA') return hasCTAButtons;
                if (section.type === 'QUICK_REPLY') return hasQuickReplyButtons;
                return false;
              })
              .map((section) => (
                <div key={section.id}>{getSectionComponent(section.type)}</div>
              ))}
          </ReactSortable>
        </div>
      )}
    </div>
  );
};

export default ButtonEditor;
